"use client"

import React, { ReactNode, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import styles from './mega-nav-mobile-new.module.scss';
import { NavigationMenu, PageReference } from '../../../typescript/response';
import { Chevron } from '../../icon/chevron';
import Button from '../../ui/button';
import LanguageSelector from '../../ui/language-selector';
import { CSLocale } from '../../../typescript/component';
import { usePageState } from '../../../context/pageContext';
import { getCachedAsset } from '../../../helper/utils';

interface MegaNavMobileProps {
  isOpen?: boolean;
  navMenu: NavigationMenu[];
  showLanguageSelector?: boolean;
  languages: CSLocale[] | null;
  currentLocale: string;
  onLocaleChange: (newLocale: string) => void;
  locations?: PageReference;
  request_quote: PageReference;
  contact_us: PageReference;
  metal_selector?: PageReference;
}

export default function MegaNavMobile({
  isOpen,
  navMenu,
  showLanguageSelector,
  languages,
  currentLocale,
  onLocaleChange,
  locations,
  request_quote,
  contact_us,
  metal_selector
}: MegaNavMobileProps) {
  const mobileMenuHeaderRef = useRef<HTMLDivElement>(null);
  const [mobileMenuStyles, setMobileMenuStyles] = useState<Record<string, string>>({});

  // TODO: Translate these strings
  const defaultHeaderLabel = "Main Menu";
  const backLabel = "Back to Main";
  const continueLabel = "Continue to";
  const defaultHeader: {
    label: string | null
    link: string | null
    backLabel: string | null
    back: boolean
  } = {
    label: null,
    link: null,
    backLabel: defaultHeaderLabel,
    back: false
  };
  const [ header, setHeader ] = useState(defaultHeader);
  const [isNavOpen, setIsNavOpen] = usePageState('isNavOpen');
  const [ prevItems, setPrevItems ] = useState<NavigationMenu[]>([]);
  const [ items, setItems ] = useState<NavigationMenu[]>(navMenu);
  const [ layout, setLayout ] = useState<string>("list");
  const [ callout, setCallout ] = useState<NavigationMenu["sub_navigation_callout"] | null>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isOpen]);

  const handleBack = () => {
    if (header.back === false) return;

    setItems(prevItems);
    setLayout("list");
    setCallout(null);

    setPrevItems([]);

    setHeader(defaultHeader);

    // setHeader((prev) => ({
    //   ...prev,
    //   label: null,
    //   link: null,
    //   backLabel: defaultHeaderLabel,
    //   back: false
    // }));
  }

  useEffect(() => {
    if (mobileMenuHeaderRef.current) {
      setMobileMenuStyles(prev => ({
        ...prev,
        "--navHeaderHeight": `${mobileMenuHeaderRef?.current?.getBoundingClientRect().height}px`
      }));
    }
  }, [items]);

  const itemsStyle: Record<string, string> = {};

  if (layout == 'grid')
    itemsStyle['--columns'] = String(items.length % 2 === 0 ? 2 : 3);

  if (!isOpen) return;

  return <>
    <div
      style={mobileMenuStyles}
      className={styles.mobileMenu}
    >
      <div
        ref={mobileMenuHeaderRef}
        className={styles.mobileMenuHeader}
      >
        <div className={styles.mobileMenuHeaderTitle}>
          <button
            className={[
              header.back ? styles.mobileMenuHeaderTitleBack : ''
            ].filter(Boolean).join(' ')}
            onClick={handleBack}
          >{header.backLabel}</button>
        </div>
        {header.link && (
          <div className={styles.mobileMenuHeaderParent}>
            <Link
              href={header.link}
              onClick={() => {
                setIsNavOpen(false);
                handleBack();
              }}
            >
              {`${continueLabel} ${header.label}`}
            </Link>
          </div>
        )}
        {/* {showLanguageSelector && (
          <LanguageSelector
            className={styles.mobileMenuHeaderLanguages}
            languages={languages}
            currentLocale={currentLocale}
            onLocaleChange={onLocaleChange}
            hideDropdown={true}
          />
        )} */}
      </div>
      <div className={styles.mobileMenuWrapper}>
        {items.length && (
          <div
            className={[
              styles.mobileMenuItems,
              styles['mobileMenuItems--' + layout]
            ].filter(Boolean).join(' ')}
            style={itemsStyle}
          >
            {items.map((item, index) => {
              const hasSubmenu = item?.sub_navigation?.length && item?.sub_navigation?.filter((s) => s?.page_reference?.some((r) => r.url != null)).length ? true : false;
              const hasCallout = item?.sub_navigation_callout?.page_reference?.length && item?.sub_navigation_callout?.page_reference?.filter((r) => r.url).length ? true : false;

              return (
                <div
                  key={index}
                  className={[
                    styles.mobileMenuItem,
                    hasSubmenu ? styles['mobileMenuItem--hasSubmenu'] : ''
                  ].filter(Boolean).join(' ')}
                >
                  <Link
                    href={item?.page_reference?.[0]?.url ?? ""}
                    className={styles.mobileMenuItemLink}
                    onClick={((e) => {
                      if (hasSubmenu) {
                        setPrevItems(items);

                        setHeader((prev) => ({
                          ...prev,
                          label: item.label,
                          link: item?.page_reference?.[0]?.url,
                          backLabel: backLabel,
                          back: true
                        }));

                        setItems(item?.sub_navigation ?? []);
                        setLayout(item?.sub_navigation_theme ?? "list")

                        if (hasCallout)
                          setCallout(item.sub_navigation_callout);
                        else
                          setCallout(null);

                        e.preventDefault();

                      // We need to close and reset the mobile menu on a link click
                      } else {
                        setIsNavOpen(false);
                        handleBack();
                      }
                    })}
                  >
                    {item?.title_image?.url && (
                      <img
                        className={styles.mobileMenuItemImage}
                        src={getCachedAsset(item?.title_image?.url)}
                        alt={`${item?.label} icon`}
                      />
                    )}
                    {item?.label}
                  </Link>
                </div>
              )
            })}
          </div>
        )}
        {callout && (
          <Callout data={callout} />
        )}
        <Footer
          request_quote={request_quote}
          metal_selector={metal_selector}
          contact_us={contact_us}
          locations={locations}
          showLanguageSelector={showLanguageSelector}
          languages={languages}
          currentLocale={currentLocale}
          onLocaleChange={onLocaleChange}
        />
      </div>
    </div>
  </>
}

const Callout = ({
  data
}: {
  data: NavigationMenu["sub_navigation_callout"]
}) => {

  return (
    <div className={styles.mobileMenuCallout}>
      <div className={styles.mobileMenuCalloutInner}>
        <div className={styles.mobileMenuCalloutImage}>
          <div className={styles.mobileMenuCalloutImageInner}>
            <img src={getCachedAsset(data?.image?.url)} alt={data?.title.replace(/<\/?[^>]+(>|$)/g, "")} />
          </div>
        </div>
        {(data?.title || data?.page_reference?.[0].summary) && (
          <div className={styles.mobileMenuCalloutCopy}>
            {data?.title && (
              <div
                className={styles.mobileMenuCalloutTitle}
                dangerouslySetInnerHTML={{__html: data.title}}
              />
            )}
            {data?.page_reference?.[0].summary && (
              <p
                className={styles.mobileMenuCalloutExcerpt}
              >{data?.page_reference?.[0].summary.substring(0, 30)}</p>
            )}
          </div>
        )}
        {data?.page_reference?.[0]?.url && (
          <div className={styles.mobileMenuCalloutLink}>
            <Link
              href={data?.page_reference?.[0]?.url}
            >{data.link_label ? data.link_label : 'Learn More'}</Link>
          </div>
        )}
      </div>
    </div>
  )
}

const Footer = ({
  request_quote,
  metal_selector,
  contact_us,
  locations,
  showLanguageSelector,
  languages,
  currentLocale,
  onLocaleChange
}: {
  request_quote?: PageReference;
  metal_selector?: PageReference;
  contact_us?: PageReference;
  locations?: PageReference;
  showLanguageSelector?: boolean;
  languages: CSLocale[] | null;
  currentLocale: string;
  onLocaleChange: (newLocale: string) => void;
}) => {
  const items: FooterColumn[] = [[
    {
      link: request_quote?.page_reference?.[0]?.url,
      label: request_quote?.label,
      class: styles.mobileMenuFooterButton
    }, {
      link: metal_selector?.page_reference?.[0]?.url,
      label: metal_selector?.label,
      class: styles.mobileMenuFooterButton
    }
  ], [
    {
      link: contact_us?.page_reference?.[0]?.url,
      label: contact_us?.label,
      icon: "mail"
    }, {
      link: locations?.page_reference?.[0]?.url,
      label: locations?.label,
      icon: "location"
    }, {
      node: showLanguageSelector && (
        <LanguageSelector
          className={styles.mobileMenuFooterLanguages}
          languages={languages}
          currentLocale={currentLocale}
          onLocaleChange={onLocaleChange}
          hideDropdown={true}
        />
      )
    }
  ]];

  {/* {showLanguageSelector && (
    <LanguageSelector
      className={styles.mobileMenuHeaderLanguages}
      languages={languages}
      currentLocale={currentLocale}
      onLocaleChange={onLocaleChange}
      hideDropdown={true}
    />
  )} */}
  
  return (
    <div className={styles.mobileMenuFooter}>
      <div className={styles.mobileMenuFooterInner}>
        {items.map((section, sectionIndex) => (
          <div key={sectionIndex} className={styles.mobileMenuFooterColumn}>
            <div className={styles.mobileMenuFooterColumnInner}>
              {section.map((item, index) => {
                if (!item.link && !item.node) return;

                const style: Record<string, string> = {};

                if (item.icon)
                  style['--icon'] = `var(--icon-${item.icon})`;

                return (
                  <div
                    key={index}
                    style={style}
                    className={[
                      styles.mobileMenuFooterItem,
                      item.icon ? styles['mobileMenuFooterItem--withIcon'] : '',
                      item.class
                    ].filter(Boolean).join(' ')}
                  >
                    {item.node ? item.node : (
                      item.link && (
                        <Link
                          href={item.link}
                        >{item?.label}</Link>
                      )
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

type FooterColumn = FooterItem[];

interface FooterItem {
  link?: string;
  label?: string;
  icon?: string;
  class?: string;
  node?: ReactNode;
}